/**
 * Extrai uma mensagem de erro de forma segura, tratando objetos, strings e outros tipos
 * @param errorMessage - Mensagem de erro que pode ser string, objeto ou outro tipo
 * @returns String contendo a mensagem de erro
 */
export const extractErrorMessage = (errorMessage: any): string => {
  if (typeof errorMessage === 'string') {
    return errorMessage;
  }
  if (Array.isArray(errorMessage)) {
    return errorMessage.map(extractErrorMessage).filter(Boolean).join('; ');
  }
  if (typeof errorMessage === 'object' && errorMessage !== null) {
    // Se for um objeto de validação do backend (Pydantic/FastAPI)
    if ('msg' in errorMessage) {
      const loc = Array.isArray(errorMessage.loc)
        ? errorMessage.loc.filter((p: unknown) => p !== 'body').join('.')
        : '';
      return loc ? `${loc}: ${errorMessage.msg}` : String(errorMessage.msg);
    }
    // Caso contrário, serializa o objeto
    return JSON.stringify(errorMessage);
  }
  return String(errorMessage || '');
};

/**
 * Extrai mensagem de erro de uma resposta de API (axios). Trata `detail` que pode
 * ser string (HTTPException com detail manual) ou array de erros Pydantic v2
 * (validação do FastAPI). Sempre devolve string segura para renderizar em JSX.
 *
 * @param err - Erro do axios ou similar
 * @param fallback - Mensagem default se nada for extraído
 */
export const extractApiErrorMessage = (err: any, fallback = 'Tente novamente'): string => {
  const detail = err?.response?.data?.detail;
  if (detail === undefined || detail === null || detail === '') return fallback;
  // Envelopes estruturados do backend: preferir a mensagem amigável ao operador
  // em vez de serializar o objeto inteiro (JSON.stringify) no toast.
  // `mensagem_operador` = FLEXAO_NAO_MAPEADA (refator concordar 2026-06-19);
  // `message` = _formatar_erro_adapter (ValueError do adapter v3).
  if (typeof detail === 'object' && !Array.isArray(detail)) {
    const mo = (detail as { mensagem_operador?: unknown }).mensagem_operador;
    if (typeof mo === 'string' && mo) return mo;
    const m = (detail as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  const msg = extractErrorMessage(detail);
  return msg || fallback;
};

/**
 * Extrai a lista de issues de validação quando o backend devolve o campo
 * ofensor de forma estruturada. Cobre os dois formatos usados no societário:
 *  - gate 422 (transformação EI→LTDA): `detail.issues = [{ codigo, campo, mensagem }]`
 *  - adapter v3 (constituição/alteração `_formatar_erro_adapter`):
 *    `detail.erros = [{ campo, mensagem, tipo }]`
 * Ambos têm `campo`/`mensagem`, então normalizamos pra uma lista só. Sem ler
 * `erros`, o front caía na mensagem genérica ("Algum dado está inconsistente")
 * e o operador não sabia QUAL campo corrigir (incidente 2026-07-22 HSA).
 * Devolve `[]` quando não há nada estruturado.
 */
export const extractApiErrorIssues = (
  err: any,
): Array<{ codigo?: string; campo?: string; mensagem?: string }> => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { issues, erros } = detail as { issues?: unknown; erros?: unknown };
    const lista = Array.isArray(issues) ? issues : erros;
    if (Array.isArray(lista)) {
      return lista.filter(
        (i): i is { codigo?: string; campo?: string; mensagem?: string } =>
          !!i && typeof i === 'object',
      );
    }
  }
  return [];
};

/**
 * Extrai o código do erro de uma resposta de API quando o backend devolve
 * `detail = { codigo: "...", message: "..." }`. Usado pra discriminar
 * cenários conhecidos (ex: `VIGENTE_HASH_MISMATCH`, `HASH_LOCK_MISMATCH`)
 * e tomar ação específica em vez de só mostrar a mensagem genérica.
 *
 * Devolve `null` quando o detail não tem `codigo` (string simples, array
 * de erros Pydantic, etc).
 */
export const extractApiErrorCode = (err: any): string | null => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && 'codigo' in detail) {
    const { codigo } = detail as { codigo: unknown };
    return typeof codigo === 'string' ? codigo : null;
  }
  return null;
};

/**
 * HTTP status code de uma resposta de erro do axios. Útil pra discriminar
 * 412 (precondition failed — vigente_hash mismatch) de 422 (validação).
 */
export const extractApiErrorStatus = (err: any): number | null => {
  const { status } = err?.response ?? {};
  return typeof status === 'number' ? status : null;
};
